import React, { useState } from 'react'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    makeStyles,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import DescriptionIcon from '@material-ui/icons/Description'
import GetAppIcon from '@material-ui/icons/GetApp'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import EventIcon from '@material-ui/icons/Event'
import AttachMoneyIcon from '@material-ui/icons/AttachMoney'
import PaymentIcon from '@material-ui/icons/Payment'
import BusinessIcon from '@material-ui/icons/Business'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import EditIcon from '@material-ui/icons/Edit'
import {
    ApartmentContract,
    ApartmentContractType,
    Company,
    ContractPaymentTerm,
    TurnContractDetails,
} from '../../models/Company/types'
import { updateContractRequest } from '../../hooks/useCompanyContracts'
import { ContractDetailsForm } from './ContractDetailsForm'
import { QuickVendorData } from './QuickAddVendorModal'

interface ContractViewModalProps {
    isOpen: boolean
    onClose: () => void
    contract: ApartmentContract | null
    /** Open straight into the details editor. */
    initialEditing?: boolean
    vendorList?: { id: number; name: string }[]
    apartmentList?: Company[]
    onSave?: (
        contractId: number,
        data: updateContractRequest,
    ) => void | Promise<unknown>
    isSaving?: boolean
    hideVendorField?: boolean
    onCreateVendor?: (data: QuickVendorData) => Promise<number>
    isCreatingVendor?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        borderRadius: theme.spacing(1.5),
        height: '90vh',
        maxHeight: '90vh',
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    iconContainer: {
        height: 44,
        width: 44,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.light + '20',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.main,
    },
    title: {
        fontSize: '1.125rem',
        fontWeight: 600,
    },
    content: {
        padding: 0,
        display: 'flex',
        height: 'calc(90vh - 140px)',
        overflow: 'hidden',
    },
    detailsPanel: {
        width: 400,
        minWidth: 400,
        borderRight: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(3),
        overflowY: 'auto',
        backgroundColor: theme.palette.background.paper,
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
        '& $sectionTitle': {
            marginBottom: 0,
        },
    },
    sectionTitle: {
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2),
    },
    fieldsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(3),
    },
    fieldItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    fieldLabel: {
        fontSize: '12px',
        color: theme.palette.text.secondary,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    fieldLabelIcon: {
        fontSize: 14,
    },
    fieldValue: {
        fontSize: '14px',
        fontWeight: 500,
        color: theme.palette.text.primary,
    },
    summaryBox: {
        backgroundColor: theme.palette.primary.light + '10',
        border: `1px solid ${theme.palette.primary.light}30`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
    },
    summaryLabel: {
        fontSize: '12px',
        fontWeight: 600,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(1),
        display: 'block',
    },
    summaryText: {
        fontSize: '14px',
        lineHeight: 1.6,
        color: theme.palette.text.primary,
    },
    summaryEmpty: {
        fontStyle: 'italic',
        color: theme.palette.text.secondary,
    },
    turnDetailsBox: {
        backgroundColor: theme.palette.secondary.light + '10',
        border: `1px solid ${theme.palette.secondary.light}40`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    turnDetailsAccordion: {
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        '&:before': {
            display: 'none',
        },
    },
    turnDetailsAccordionSummary: {
        minHeight: 40,
        padding: theme.spacing(0, 1.5),
        '&.Mui-expanded': {
            minHeight: 40,
        },
        '& .MuiAccordionSummary-content': {
            margin: theme.spacing(1, 0),
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: theme.spacing(1, 0),
        },
    },
    turnDetailsAccordionContent: {
        padding: theme.spacing(0, 1.5, 1.5),
    },
    viewerPanel: {
        flex: 1,
        minWidth: 0,
        backgroundColor: theme.palette.grey[100],
    },
    iframe: {
        width: '100%',
        height: '100%',
        border: 'none',
    },
    noDocument: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    certRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(0.5),
        '&:not(:last-child)': {
            marginBottom: theme.spacing(1),
        },
    },
    certName: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontSize: '13px',
        fontWeight: 500,
    },
    actions: {
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        justifyContent: 'space-between',
    },
}))

// Payment term display labels
const PAYMENT_TERM_LABELS: Record<ContractPaymentTerm, string> = {
    [ContractPaymentTerm.MONTHLY]: 'Monthly',
    [ContractPaymentTerm.QUARTERLY]: 'Quarterly',
    [ContractPaymentTerm.ANNUALLY]: 'Annually',
    [ContractPaymentTerm.ONE_TIME]: 'One-Time',
    [ContractPaymentTerm.PER_JOB]: 'Per Job',
}

const CONTRACT_TYPE_LABELS: Record<ApartmentContractType, string> = {
    [ApartmentContractType.CERTIFICATE]: 'Certificates',
    [ApartmentContractType.TURN]: 'Turn Vendors',
    [ApartmentContractType.GENERAL_VENDOR]: 'General Vendor',
}

const PRIMARY_TURN_DETAIL_FIELDS: {
    label: string
    key: keyof TurnContractDetails
}[] = [
    { label: 'Turn Window', key: 'turn_window' },
    { label: 'Pricing Structure', key: 'pricing_structure' },
    { label: 'Per Unit Price', key: 'per_unit_price' },
    { label: 'Per Bed Price', key: 'per_bed_price' },
    { label: 'Service Scope', key: 'service_scope' },
]

const SECONDARY_TURN_DETAIL_FIELDS: {
    label: string
    key: keyof TurnContractDetails
}[] = [
    { label: 'Inspection Requirements', key: 'inspection_requirements' },
    { label: 'Penalties', key: 'penalties' },
    { label: 'Staffing Requirements', key: 'staffing_requirements' },
    { label: 'Exclusions', key: 'exclusions' },
    { label: 'Trades Covered', key: 'trades_covered' },
    { label: 'Volume Assumptions', key: 'volume_assumptions' },
    { label: 'Important Deadlines', key: 'important_deadlines' },
]

export const ContractViewModal: React.FC<ContractViewModalProps> = ({
    isOpen,
    onClose,
    contract,
    initialEditing = false,
    vendorList = [],
    apartmentList = [],
    onSave,
    isSaving = false,
    hideVendorField = false,
    onCreateVendor,
    isCreatingVendor = false,
}) => {
    const classes = useStyles()
    const openKey = isOpen
        ? `${contract?.id ?? 0}-${initialEditing ? 'edit' : 'view'}`
        : 'closed'
    const [sessionKey, setSessionKey] = useState(openKey)
    const [editing, setEditing] = useState(isOpen && initialEditing)

    if (sessionKey !== openKey) {
        setSessionKey(openKey)
        setEditing(isOpen && initialEditing)
    }

    if (!contract) return null

    const showEditor = editing && !!onSave

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '—'
        // Parse date parts directly to avoid timezone conversion issues
        const [year, month, day] = dateString.split('T')[0].split('-')
        const months = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ]
        return `${months[parseInt(month, 10) - 1]} ${parseInt(
            day,
            10,
        )}, ${year}`
    }

    const formatCurrency = (value: number | null) => {
        if (value === null) return '—'
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value)
    }

    const formatContractType = (type?: ApartmentContractType) => {
        if (!type) {
            return CONTRACT_TYPE_LABELS[ApartmentContractType.GENERAL_VENDOR]
        }
        return CONTRACT_TYPE_LABELS[type] || type
    }

    const formatTurnDetailValue = (
        value: TurnContractDetails[keyof TurnContractDetails],
    ) => {
        if (Array.isArray(value)) {
            return value.length > 0 ? value.join(', ') : ''
        }
        return value || ''
    }

    const contractType =
        contract.contract_type ?? ApartmentContractType.GENERAL_VENDOR
    const mapVisibleTurnDetails = (fields: typeof PRIMARY_TURN_DETAIL_FIELDS) =>
        fields
            .map((field) => ({
                label: field.label,
                value: formatTurnDetailValue(
                    contract.ai_extracted_data?.turn_details?.[field.key],
                ),
            }))
            .filter((field) => field.value)
    const primaryTurnDetails =
        contractType === ApartmentContractType.TURN
            ? mapVisibleTurnDetails(PRIMARY_TURN_DETAIL_FIELDS)
            : []
    const secondaryTurnDetails =
        contractType === ApartmentContractType.TURN
            ? mapVisibleTurnDetails(SECONDARY_TURN_DETAIL_FIELDS)
            : []
    const hasTurnDetails =
        primaryTurnDetails.length > 0 || secondaryTurnDetails.length > 0

    // Check if the file is a PDF (can be displayed directly in iframe)
    const isPdf = contract.name.toLowerCase().endsWith('.pdf')

    // Get the viewer URL - use Google Docs Viewer for non-PDF files
    const getViewerUrl = () => {
        if (!contract.file) return ''
        if (isPdf) {
            return contract.file
        }
        return `https://docs.google.com/gview?url=${encodeURIComponent(
            contract.file,
        )}&embedded=true`
    }

    const handleDownload = () => {
        if (contract.file) {
            const link = window.document.createElement('a')
            link.href = contract.file
            link.download = contract.name
            link.click()
        }
    }

    const handleOpenInNewTab = () => {
        if (contract.file) {
            window.open(contract.file, '_blank')
        }
    }

    return (
            <Dialog
            open={isOpen}
            onClose={() => {
                if (!isSaving) onClose()
            }}
            maxWidth="lg"
            fullWidth
            PaperProps={{ className: classes.paper }}
        >
            {/* Header */}
            <Box className={classes.header}>
                <Box className={classes.headerLeft}>
                    <Box className={classes.iconContainer}>
                        <DescriptionIcon />
                    </Box>
                    <Box>
                        <Typography className={classes.title}>
                            {contract.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Created {formatDate(contract.created_date)}
                        </Typography>
                    </Box>
                </Box>
                <IconButton size="small" onClick={onClose} disabled={isSaving}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <DialogContent className={classes.content}>
                {/* Left: Details Panel */}
                <Box className={classes.detailsPanel}>
                    <Box className={classes.sectionHeader}>
                        <Typography className={classes.sectionTitle}>
                            Contract Details
                        </Typography>
                        {onSave && !showEditor && (
                            <Button
                                size="small"
                                color="primary"
                                startIcon={<EditIcon />}
                                onClick={() => setEditing(true)}
                            >
                                Edit
                            </Button>
                        )}
                    </Box>

                    {showEditor && onSave ? (
                        <ContractDetailsForm
                            contract={contract}
                            vendorList={vendorList}
                            apartmentList={apartmentList}
                            onSave={onSave}
                            isSaving={isSaving}
                            hideVendorField={hideVendorField}
                            onCreateVendor={onCreateVendor}
                            isCreatingVendor={isCreatingVendor}
                            onCancel={() => setEditing(false)}
                            onSaved={() => setEditing(false)}
                        />
                    ) : (
                        <>
                    <Box className={classes.fieldsContainer}>
                        <Box className={classes.fieldItem}>
                            <Typography className={classes.fieldLabel}>
                                <BusinessIcon
                                    className={classes.fieldLabelIcon}
                                />
                                Vendor
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {contract.vendor?.name || '—'}
                            </Typography>
                        </Box>

                        <Box className={classes.fieldItem}>
                            <Typography className={classes.fieldLabel}>
                                <DescriptionIcon
                                    className={classes.fieldLabelIcon}
                                />
                                {contract.contract_type ===
                                ApartmentContractType.CERTIFICATE
                                    ? 'Certificate Type'
                                    : 'Contract Type'}
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {(contract.contract_type ===
                                ApartmentContractType.CERTIFICATE
                                    ? contract.organization_certificate_type_detail
                                    : contract.organization_contract_type_detail
                                )?.name || 'Untyped'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {formatContractType(contract.contract_type)}
                            </Typography>
                        </Box>

                        {(contract.apartment_names ?? []).length > 0 && (
                            <Box className={classes.fieldItem}>
                                <Typography className={classes.fieldLabel}>
                                    <BusinessIcon
                                        className={classes.fieldLabelIcon}
                                    />
                                    Properties
                                </Typography>
                                {(() => {
                                    const names = contract.apartment_names ?? []
                                    const maxVisible = 2
                                    if (names.length <= maxVisible) {
                                        return (
                                            <Typography
                                                className={classes.fieldValue}
                                            >
                                                {names.join(', ')}
                                            </Typography>
                                        )
                                    }
                                    const visible = names
                                        .slice(0, maxVisible)
                                        .join(', ')
                                    const rest = names.length - maxVisible
                                    return (
                                        <Tooltip
                                            title={names.join(', ')}
                                            placement="top"
                                            enterDelay={200}
                                        >
                                            <Typography
                                                className={classes.fieldValue}
                                                style={{ cursor: 'default' }}
                                            >
                                                {visible}
                                                <Typography
                                                    component="span"
                                                    color="primary"
                                                    style={{
                                                        fontWeight: 600,
                                                        marginLeft: 4,
                                                    }}
                                                >
                                                    +{rest} more
                                                </Typography>
                                            </Typography>
                                        </Tooltip>
                                    )
                                })()}
                            </Box>
                        )}

                        <Box className={classes.fieldItem}>
                            <Typography className={classes.fieldLabel}>
                                <AttachMoneyIcon
                                    className={classes.fieldLabelIcon}
                                />
                                Value
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {formatCurrency(contract.payment_amount)}
                            </Typography>
                        </Box>

                        <Box className={classes.fieldItem}>
                            <Typography className={classes.fieldLabel}>
                                <EventIcon className={classes.fieldLabelIcon} />
                                Start Date
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {formatDate(contract.start_date)}
                            </Typography>
                        </Box>

                        <Box className={classes.fieldItem}>
                            <Typography className={classes.fieldLabel}>
                                <EventIcon className={classes.fieldLabelIcon} />
                                End Date
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {formatDate(contract.end_date)}
                            </Typography>
                        </Box>

                        <Box className={classes.fieldItem}>
                            <Typography className={classes.fieldLabel}>
                                <PaymentIcon
                                    className={classes.fieldLabelIcon}
                                />
                                Payment Term
                            </Typography>
                            <Typography className={classes.fieldValue}>
                                {contract.payment_term
                                    ? PAYMENT_TERM_LABELS[contract.payment_term]
                                    : '—'}
                            </Typography>
                        </Box>
                    </Box>

                    {/* AI Summary */}
                    <Typography className={classes.sectionTitle}>
                        AI Summary
                    </Typography>
                    <Box className={classes.summaryBox}>
                        <Typography
                            className={`${classes.summaryText} ${
                                !contract.ai_summary ? classes.summaryEmpty : ''
                            }`}
                        >
                            {contract.ai_summary ||
                                'No summary available yet. The contract may still be processing.'}
                        </Typography>
                    </Box>

                    {hasTurnDetails && (
                        <>
                            <Typography
                                className={classes.sectionTitle}
                                style={{ marginTop: 24 }}
                            >
                                Turn Details
                            </Typography>
                            {primaryTurnDetails.length > 0 && (
                                <Box className={classes.turnDetailsBox}>
                                    <Box className={classes.fieldsContainer}>
                                        {primaryTurnDetails.map((field) => (
                                            <Box
                                                key={field.label}
                                                className={classes.fieldItem}
                                            >
                                                <Typography
                                                    className={
                                                        classes.fieldLabel
                                                    }
                                                >
                                                    {field.label}
                                                </Typography>
                                                <Typography
                                                    className={
                                                        classes.fieldValue
                                                    }
                                                >
                                                    {field.value}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            {secondaryTurnDetails.length > 0 && (
                                <Accordion
                                    className={classes.turnDetailsAccordion}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        className={
                                            classes.turnDetailsAccordionSummary
                                        }
                                    >
                                        <Typography
                                            className={classes.fieldValue}
                                        >
                                            More Turn Details
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails
                                        className={
                                            classes.turnDetailsAccordionContent
                                        }
                                    >
                                        <Box
                                            className={classes.fieldsContainer}
                                            style={{ marginBottom: 0 }}
                                        >
                                            {secondaryTurnDetails.map(
                                                (field) => (
                                                    <Box
                                                        key={field.label}
                                                        className={
                                                            classes.fieldItem
                                                        }
                                                    >
                                                        <Typography
                                                            className={
                                                                classes.fieldLabel
                                                            }
                                                        >
                                                            {field.label}
                                                        </Typography>
                                                        <Typography
                                                            className={
                                                                classes.fieldValue
                                                            }
                                                        >
                                                            {field.value}
                                                        </Typography>
                                                    </Box>
                                                ),
                                            )}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                        </>
                    )}

                    {/* Auto-Renew Info */}
                    {contract.auto_renew && (
                        <>
                            <Typography
                                className={classes.sectionTitle}
                                style={{ marginTop: 24 }}
                            >
                                Auto-Renew
                            </Typography>
                            <Box className={classes.fieldsContainer}>
                                <Box className={classes.fieldItem}>
                                    <Typography className={classes.fieldLabel}>
                                        <AutorenewIcon
                                            className={classes.fieldLabelIcon}
                                        />
                                        Auto-Renew
                                    </Typography>
                                    <Typography className={classes.fieldValue}>
                                        Enabled
                                    </Typography>
                                </Box>
                                {contract.end_date && (
                                    <Box className={classes.fieldItem}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            Renews On
                                        </Typography>
                                        <Typography
                                            className={classes.fieldValue}
                                        >
                                            {formatDate(contract.end_date)}
                                        </Typography>
                                    </Box>
                                )}
                                {contract.renewal_term_months && (
                                    <Box className={classes.fieldItem}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            Renewal Term
                                        </Typography>
                                        <Typography
                                            className={classes.fieldValue}
                                        >
                                            {contract.renewal_term_months}{' '}
                                            month(s)
                                        </Typography>
                                    </Box>
                                )}
                                {contract.notice_lead_days && (
                                    <Box className={classes.fieldItem}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            Notice Period
                                        </Typography>
                                        <Typography
                                            className={classes.fieldValue}
                                        >
                                            {contract.notice_lead_days} days
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}
                        </>
                    )}
                </Box>

                {/* Right: Document Viewer */}
                <Box className={classes.viewerPanel}>
                    {contract.file ? (
                        <iframe
                            src={getViewerUrl()}
                            className={classes.iframe}
                            title={contract.name}
                        />
                    ) : (
                        <Box className={classes.noDocument}>
                            <Typography color="textSecondary">
                                No document available
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions className={classes.actions}>
                <Button
                    onClick={handleOpenInNewTab}
                    variant="outlined"
                    color="default"
                    startIcon={<OpenInNewIcon />}
                >
                    Open in New Tab
                </Button>
                <Box display="flex" style={{ gap: 8 }}>
                    <Button
                        onClick={onClose}
                        variant="outlined"
                        color="default"
                        disabled={isSaving}
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleDownload}
                        variant="contained"
                        color="primary"
                        startIcon={<GetAppIcon />}
                    >
                        Download
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}
